import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Clock3,
  CloudCog,
  Database,
  FilterX,
  RefreshCw,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import {
  deleteUserCloudSyncLog,
  getUserCloudSyncLog,
  runUserCloudIncrementalSync,
  runUserCloudSync,
} from '../api/api';
import { ConfirmDialog, Toast } from '../components/ui';
import styles from './MachineUserCloudSyncLog.module.scss';

const EVENT_LABELS = {
  machine_updated: 'Machine updated',
  registered: 'Registered',
  sync_error: 'Sync error',
  sync_round: 'Sync round',
  company_added: 'Company added',
  company_updated: 'Company updated',
  company_removed: 'Company removed',
};

const EVENT_BADGE_CLASS_KEY = {
  machine_updated: 'eventUpdated',
  registered: 'eventRegistered',
  sync_error: 'eventError',
  sync_round: 'eventRound',
  company_added: 'eventCompanyAdded',
  company_updated: 'eventCompanyUpdated',
  company_removed: 'eventCompanyRemoved',
};

const FIELD_LABELS = {
  nickname: 'Nickname',
  company_name: 'Company name',
  name: 'Name',
  tag: 'Tag',
  is_partner: 'Partner',
  partner_company: 'Partner company',
};

// Fields whose values are booleans read better as Partner/Customer than true/false.
const BOOLEAN_FIELD_LABELS = {
  is_partner: { true: 'Partner', false: 'Customer' },
};

const SOURCE_LABELS = {
  periodic_sync: 'Scheduled sync',
  periodic_full_sync: 'Scheduled full sync',
  status_post: 'Status update',
  manual: 'Manual sync',
};

const displayValue = value =>
  value === null || value === undefined || value === '' ? '(empty)' : String(value);

const displayRequestBody = body =>
  body === null || body === undefined ? '(empty)' : JSON.stringify(body);

function MachineUserCloudSyncLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncingMode, setSyncingMode] = useState('');
  const [confirmFullSync, setConfirmFullSync] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState('');

  const [serialFilter, setSerialFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');

  useEffect(() => {
    fetchLog();
  }, []);

  const fetchLog = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError('');
      const response = await getUserCloudSyncLog();
      setEntries(response.data?.entries || []);
    } catch (fetchError) {
      console.error('Error fetching sync history:', fetchError);
      setError('Could not load synchronization history.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const serials = useMemo(() => {
    const unique = new Set(entries.map(entry => entry.serial).filter(Boolean));
    return [...unique].sort();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const fromMs = fromTime ? new Date(fromTime).getTime() : null;
    const toMs = toTime ? new Date(toTime).getTime() : null;

    return entries
      .filter(entry => {
        const timeMs = (entry.time || 0) * 1000;
        if (serialFilter && entry.serial !== serialFilter) {
          return false;
        }
        if (eventFilter && entry.event !== eventFilter) {
          return false;
        }
        if (fromMs !== null && timeMs < fromMs) {
          return false;
        }
        if (toMs !== null && timeMs > toMs) {
          return false;
        }
        return true;
      })
      .reverse();
  }, [entries, serialFilter, eventFilter, fromTime, toTime]);

  const latestRound = useMemo(
    () => [...entries].reverse().find(entry => entry.event === 'sync_round'),
    [entries]
  );
  const errorCount = useMemo(
    () => entries.filter(entry => entry.event === 'sync_error').length,
    [entries]
  );
  const hasFilters = Boolean(serialFilter || eventFilter || fromTime || toTime);

  const clearFilters = () => {
    setSerialFilter('');
    setEventFilter('');
    setFromTime('');
    setToTime('');
  };

  const formatTime = time => (time ? new Date(time * 1000).toLocaleString() : 'No sync recorded');

  const runSync = async mode => {
    try {
      setSyncingMode(mode);
      setSyncResult(null);
      setError('');
      const response =
        mode === 'incremental' ? await runUserCloudIncrementalSync() : await runUserCloudSync();
      const result = response.data || {};
      const errors = result.errors || 0;
      const details =
        mode === 'incremental'
          ? `${result.updated || 0} machines updated from ${result.systems || 0} changed systems and ${result.companies || 0} changed companies.`
          : `${result.updated || 0} machines updated and ${result.registered || 0} machines registered.`;

      setSyncResult({
        tone: errors > 0 ? 'warning' : 'success',
        title:
          errors > 0
            ? `Sync completed with ${errors} error${errors === 1 ? '' : 's'}`
            : 'Synchronization completed',
        details,
      });
      setConfirmFullSync(false);
      await fetchLog({ silent: true });
    } catch (syncError) {
      console.error(`Error running ${mode} sync:`, syncError);
      setError(syncError.response?.data?.detail || `Could not run ${mode} synchronization.`);
    } finally {
      setSyncingMode('');
    }
  };

  const deleteAllLogs = async () => {
    try {
      setDeleting(true);
      setError('');
      await deleteUserCloudSyncLog();
      setEntries([]);
      setConfirmDelete(false);
    } catch (deleteError) {
      console.error('Error deleting sync history:', deleteError);
      setError('Could not delete synchronization history.');
    } finally {
      setDeleting(false);
    }
  };

  const displayFieldValue = (field, value) => {
    const map = BOOLEAN_FIELD_LABELS[field];
    if (map && typeof value === 'boolean') {
      return map[String(value)];
    }
    return displayValue(value);
  };

  const renderChanges = changes =>
    Object.entries(changes || {}).map(([field, change]) => (
      <div key={field} className={styles.changeLine}>
        <span className={styles.changeField}>{FIELD_LABELS[field] || field}:</span>{' '}
        <span className={styles.changeFrom}>{displayFieldValue(field, change?.from)}</span>
        {' → '}
        <span className={styles.changeTo}>{displayFieldValue(field, change?.to)}</span>
      </div>
    ));

  const renderDetails = entry => {
    if (entry.event === 'machine_updated') {
      return renderChanges(entry.changes);
    }
    if (entry.event === 'company_added') {
      const role = entry.is_partner ? 'Partner' : 'Customer';
      const parts = [`Added as ${role}`];
      if (entry.tag) {
        parts.push(`tagged “${entry.tag}”`);
      }
      if (entry.partner_company) {
        parts.push(`under partner ${entry.partner_company}`);
      }
      return parts.join(' · ');
    }
    if (entry.event === 'company_updated') {
      return renderChanges(entry.changes);
    }
    if (entry.event === 'company_removed') {
      return 'Removed on the User Cloud';
    }
    if (entry.event === 'registered') {
      const source = SOURCE_LABELS[entry.source] || entry.source || 'Unknown source';
      return `Registered on User Cloud via ${source}`;
    }
    if (entry.event === 'sync_error') {
      return (
        <div>
          {entry.request && (
            <div className={styles.changeLine}>
              <span className={styles.changeField}>Request:</span>{' '}
              <span className={styles.detailText}>{entry.request}</span>
            </div>
          )}
          {entry.request_body !== null && entry.request_body !== undefined && (
            <div className={styles.changeLine}>
              <span className={styles.changeField}>Body:</span>{' '}
              <span className={styles.detailText}>{displayRequestBody(entry.request_body)}</span>
            </div>
          )}
          <div className={styles.changeLine}>
            <span className={styles.changeField}>Status:</span> {entry.status ?? 'No response'}
          </div>
          <div className={styles.changeLine}>
            <span className={styles.changeField}>Response:</span>{' '}
            <span className={styles.detailText}>{displayValue(entry.response)}</span>
          </div>
        </div>
      );
    }
    if (entry.event === 'sync_round') {
      const source = SOURCE_LABELS[entry.source] || entry.source || 'Unknown source';
      return (
        `Checked ${entry.machines || 0} machines: ${entry.updated || 0} updated, ` +
        `${entry.registered || 0} registered, ${entry.errors || 0} errors · ${source}`
      );
    }
    return JSON.stringify(entry);
  };

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div className={styles.headerIdentity}>
          <span className={styles.headerIcon}>
            <CloudCog size={23} />
          </span>
          <div>
            <p>User Cloud integration</p>
            <h1>Cloud synchronization</h1>
            <span>Keep companies and machine records aligned with the User Cloud.</span>
          </div>
        </div>
        <button className={styles.refreshButton} onClick={() => fetchLog()} disabled={loading}>
          <RefreshCw size={16} className={loading ? styles.spinning : ''} />
          {loading ? 'Refreshing' : 'Refresh history'}
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}

      <section className={styles.overviewGrid} aria-label="Synchronization overview">
        <article className={styles.overviewCard}>
          <span className={styles.overviewIcon}>
            <Clock3 size={18} />
          </span>
          <div>
            <span>Last sync round</span>
            <strong>{formatTime(latestRound?.time)}</strong>
          </div>
        </article>
        <article className={styles.overviewCard}>
          <span className={styles.overviewIcon}>
            <Database size={18} />
          </span>
          <div>
            <span>Machines in latest round</span>
            <strong>{latestRound?.machines ?? '—'}</strong>
          </div>
        </article>
        <article className={styles.overviewCard}>
          <span
            className={`${styles.overviewIcon} ${errorCount ? styles.overviewIconWarning : ''}`}
          >
            {errorCount ? <TriangleAlert size={18} /> : <CheckCircle2 size={18} />}
          </span>
          <div>
            <span>Errors in retained history</span>
            <strong>{errorCount}</strong>
          </div>
        </article>
      </section>

      <section className={styles.syncPanel}>
        <div className={styles.sectionHeading}>
          <div>
            <p>Run synchronization</p>
            <h2>Choose how much to reconcile</h2>
          </div>
          <span>Incremental sync is recommended for normal operation.</span>
        </div>

        <div className={styles.syncStrategies}>
          <article className={`${styles.strategyCard} ${styles.strategyRecommended}`}>
            <div className={styles.strategyIcon}>
              <Zap size={20} />
            </div>
            <div className={styles.strategyCopy}>
              <span className={styles.recommendedBadge}>Recommended</span>
              <h3>Sync recent changes</h3>
              <p>Fetch only companies and systems changed since the previous synchronization.</p>
            </div>
            <button
              className={styles.primaryAction}
              onClick={() => runSync('incremental')}
              disabled={Boolean(syncingMode)}
            >
              <RefreshCw
                size={16}
                className={syncingMode === 'incremental' ? styles.spinning : ''}
              />
              {syncingMode === 'incremental' ? 'Syncing changes…' : 'Sync changes'}
            </button>
          </article>

          <article className={styles.strategyCard}>
            <div className={`${styles.strategyIcon} ${styles.strategyIconNeutral}`}>
              <RotateCcw size={20} />
            </div>
            <div className={styles.strategyCopy}>
              <h3>Full reconciliation</h3>
              <p>Re-check every known machine. Use this after recovery or suspected data drift.</p>
            </div>
            <button
              className={styles.secondaryAction}
              onClick={() => setConfirmFullSync(true)}
              disabled={Boolean(syncingMode)}
            >
              Review full sync
            </button>
          </article>
        </div>

        {syncResult && (
          <Toast
            tone={syncResult.tone === 'warning' ? 'warning' : 'success'}
            onClose={() => setSyncResult(null)}
          >
            <strong>{syncResult.title}</strong>
            <span>{syncResult.details}</span>
          </Toast>
        )}
      </section>

      <ConfirmDialog
        open={confirmFullSync}
        title="Run a full reconciliation?"
        description="This checks every machine and can take longer than an incremental sync."
        confirmLabel="Run full sync"
        busyLabel="Reconciling…"
        tone="primary"
        busy={syncingMode === 'full'}
        confirmDisabled={Boolean(syncingMode)}
        onConfirm={() => runSync('full')}
        onCancel={() => setConfirmFullSync(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete all synchronization history?"
        description="This does not affect machines or companies and cannot be undone."
        confirmLabel="Delete all history"
        busyLabel="Deleting…"
        busy={deleting}
        onConfirm={deleteAllLogs}
        onCancel={() => setConfirmDelete(false)}
      />

      <section className={styles.historyPanel}>
        <div className={styles.historyHeader}>
          <div>
            <p>Audit trail</p>
            <h2>Synchronization history</h2>
          </div>
          <button
            className={styles.deleteHistoryButton}
            onClick={() => setConfirmDelete(true)}
            disabled={deleting || entries.length === 0}
          >
            <Trash2 size={15} />
            Delete history
          </button>
        </div>

        <div className={styles.filtersSection}>
          <label className={styles.filterItem}>
            <span>Machine</span>
            <select value={serialFilter} onChange={event => setSerialFilter(event.target.value)}>
              <option value="">All machines</option>
              {serials.map(serial => (
                <option key={serial} value={serial}>
                  {serial}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.filterItem}>
            <span>Event</span>
            <select value={eventFilter} onChange={event => setEventFilter(event.target.value)}>
              <option value="">All events</option>
              <option value="machine_updated">Machine updated</option>
              <option value="registered">Registered</option>
              <option value="company_added">Company added</option>
              <option value="company_updated">Company updated</option>
              <option value="company_removed">Company removed</option>
              <option value="sync_error">Sync error</option>
              <option value="sync_round">Sync round</option>
            </select>
          </label>
          <label className={styles.filterItem}>
            <span>From</span>
            <input
              type="datetime-local"
              value={fromTime}
              onChange={event => setFromTime(event.target.value)}
            />
          </label>
          <label className={styles.filterItem}>
            <span>To</span>
            <input
              type="datetime-local"
              value={toTime}
              onChange={event => setToTime(event.target.value)}
            />
          </label>
          <button className={styles.clearButton} onClick={clearFilters} disabled={!hasFilters}>
            <FilterX size={15} />
            Clear filters
          </button>
        </div>

        <div className={styles.resultCount}>
          <span>
            Showing <strong>{filteredEntries.length}</strong> of {entries.length} events
          </span>
          {hasFilters && <span>Filters applied</span>}
        </div>

        {loading && entries.length === 0 ? (
          <div className={styles.emptyState}>Loading synchronization history…</div>
        ) : filteredEntries.length === 0 ? (
          <div className={styles.emptyState}>
            <Clock3 size={24} />
            <strong>
              {entries.length ? 'No events match these filters' : 'No sync history yet'}
            </strong>
            <span>
              {entries.length
                ? 'Change or clear the filters to see more events.'
                : 'Run a synchronization to create the first audit entry.'}
            </span>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.logTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Machine / Company</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry, index) => (
                  <tr key={`${entry.time}-${entry.serial}-${index}`}>
                    <td data-label="Time" className={styles.timeCell}>
                      {formatTime(entry.time)}
                    </td>
                    <td data-label="Event">
                      <span
                        className={`${styles.eventBadge} ${
                          styles[EVENT_BADGE_CLASS_KEY[entry.event]] || styles.eventRegistered
                        }`}
                      >
                        {EVENT_LABELS[entry.event] || entry.event}
                      </span>
                    </td>
                    <td data-label="Machine / Company" className={styles.serialCell}>
                      {entry.serial || entry.name || '—'}
                    </td>
                    <td data-label="Details">{renderDetails(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default MachineUserCloudSyncLog;
